import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { MessageCircle, MapPin, Phone, Mail } from 'lucide-react';

function InstagramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function FacebookIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

export default function FooterSection() {
  return (
    <footer className="bg-dark text-white" id="contact">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="xl:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <AppLogo size={36} />
              <span className="font-extrabold text-lg text-white">
                Drip N Dry <span className="text-primary">Curaçao</span>
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Premium car wash at your location or ours. Serving all of Curaçao with a smile and a shine.
            </p>
            <div className="flex gap-3">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-primary flex items-center justify-center transition-colors" aria-label="Instagram">
                <InstagramIcon size={16} />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-primary flex items-center justify-center transition-colors" aria-label="Facebook">
                <FacebookIcon size={16} />
              </a>
              <a href="https://wa.me/5999000000" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-green-500/80 hover:bg-green-500 flex items-center justify-center transition-colors" aria-label="WhatsApp">
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-widest">Services</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Outside Wash', href: '/services' },
                { label: 'Inside + Outside', href: '/services' },
                { label: 'Full Detail', href: '/services' },
                { label: 'Wax Protection', href: '/services' },
                { label: 'Seat Shampoo', href: '/services' },
                { label: 'Monthly Subscription', href: '/subscription' },
              ]?.map((s) => (
                <li key={`footer-svc-${s.label}`}>
                  <Link href={s.href} className="text-white/60 hover:text-white text-sm transition-colors">{s.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-widest">Company</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Book Appointment', href: '/book-appointment' },
                { label: 'Sign In / Register', href: '/sign-up-login-screen' },
                { label: 'Loyalty Program', href: '/loyalty' },
                { label: 'Subscription', href: '/subscription' },
                { label: 'About Us', href: '/about' },
                { label: 'Contact', href: '/contact' },
              ]?.map((link) => (
                <li key={`footer-link-${link?.label}`}>
                  <Link href={link?.href} className="text-white/60 hover:text-white text-sm transition-colors">{link?.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-widest">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-white/60">
                <MapPin size={15} className="text-primary flex-shrink-0 mt-0.5" />
                Rooi Santu, Curaçao
              </li>
              <li className="flex items-start gap-3 text-sm text-white/60">
                <Phone size={15} className="text-primary flex-shrink-0 mt-0.5" />
                +599 9 000 0000 (placeholder)
              </li>
              <li className="flex items-start gap-3 text-sm text-white/60">
                <Mail size={15} className="text-primary flex-shrink-0 mt-0.5" />
                info@dripndrycuracao.com
              </li>
            </ul>
            <a
              href="https://wa.me/5999000000"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              <MessageCircle size={16} />
              Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* Legal links */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5">
            {[
              { label: 'Privacy Policy', href: '/privacy-policy' },
              { label: 'Terms & Conditions', href: '/terms-and-conditions' },
              { label: 'Cancellation Policy', href: '/cancellation-policy' },
              { label: 'Refund Policy', href: '/refund-policy' },
              { label: 'Subscription Terms', href: '/subscription-terms' },
              { label: 'Loyalty Terms', href: '/loyalty-terms' },
            ].map((link) => (
              <Link key={`legal-${link.label}`} href={link.href} className="text-xs text-white/40 hover:text-white/70 transition-colors">
                {link.label}
              </Link>
            ))}
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
            <p>© 2026 Drip N Dry Curaçao. All rights reserved.</p>
            <p>dripndrycuracao.com (domain placeholder)</p>
          </div>
        </div>
      </div>
      {/* Sticky WhatsApp */}
      <a
        href="https://wa.me/5999000000"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-xl transition-all duration-200 hover:scale-110"
        aria-label="Contact on WhatsApp"
      >
        <MessageCircle size={24} className="text-white" />
      </a>
    </footer>
  );
}